/**
 * Items to Replace - Missing & Damaged Items
 * Admin & Super Admin: Replace missing, process damaged costs, mark maintained, Full Report
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { superAdminAPI } from "utils/api";

const formatCurrency = (amount, currency = "EGP") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 0 }).format(
    amount || 0
  );

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

const filterInputHeight = { "& .MuiInputBase-root": { height: 44 } };

function ItemsToProcess() {
  const navigate = useNavigate();
  const isSuperAdmin = window.location.pathname.startsWith("/super-admin");
  const backRoute = isSuperAdmin ? "/super-admin/reservations" : "/admin/dashboard";

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [report, setReport] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [damagedCosts, setDamagedCosts] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [processSearch, setProcessSearch] = useState("");
  const [processFilterType, setProcessFilterType] = useState("all");
  const [processFilterStatus, setProcessFilterStatus] = useState("all");

  useEffect(() => {
    fetchReservations();
  }, []);

  useEffect(() => {
    if (tabValue === 1) fetchReport();
  }, [tabValue]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const res = await superAdminAPI.getItemsToProcess();
      setReservations(res.reservations || []);
    } catch (error) {
      console.error("Error fetching items:", error);
      setMessage({ type: "error", text: error.message || "Failed to load" });
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      setReportLoading(true);
      const res = await superAdminAPI.getItemsReport();
      setReport(res.report || []);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleOpenDetails = (reservation) => {
    setSelectedReservation(reservation);
    const costs = {};
    (reservation.checkOut?.damagedItems || []).forEach((d, i) => {
      if (d.cost != null && d.cost > 0) costs[i] = d.cost;
    });
    setDamagedCosts(costs);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReservation(null);
    setDamagedCosts({});
  };

  const handleMarkReplaced = async (reservationId, index) => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      const res = await superAdminAPI.markMissingItemReplaced(reservationId, index);
      setMessage({ type: "success", text: "Item marked as replaced" });
      if (res.reservation) setSelectedReservation(res.reservation);
      await fetchReservations();
      if (tabValue === 1) await fetchReport();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to update" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDamagedMaintained = async (reservationId, index) => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      const res = await superAdminAPI.markDamagedItemMaintained(reservationId, index);
      setMessage({ type: "success", text: "Item marked as maintained" });
      if (res.reservation) setSelectedReservation(res.reservation);
      await fetchReservations();
      if (tabValue === 1) await fetchReport();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to update" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDamagedCostChange = (index, value) => {
    const num = parseFloat(value);
    setDamagedCosts((prev) => ({
      ...prev,
      [index]: isNaN(num) || num < 0 ? "" : num,
    }));
  };

  const handleSendDamagedCosts = async () => {
    if (!selectedReservation) return;
    const items = selectedReservation.checkOut?.damagedItems || [];
    const costs = items
      .map((_, i) =>
        damagedCosts[i] != null && damagedCosts[i] > 0
          ? { index: i, cost: Number(damagedCosts[i]) }
          : null
      )
      .filter(Boolean);
    if (costs.length === 0) {
      setMessage({ type: "error", text: "Enter at least one cost for damaged items" });
      return;
    }
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      await superAdminAPI.submitDamagedItemsCosts(selectedReservation._id, costs);
      setMessage({ type: "success", text: "Costs sent to customer. Email delivered." });
      handleCloseDialog();
      await fetchReservations();
      if (tabValue === 1) await fetchReport();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to send" });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredReservations = useMemo(() => {
    return reservations.filter((r) => {
      if (processSearch) {
        const term = processSearch.toLowerCase();
        const match =
          (r.reservationNumber || "").toLowerCase().includes(term) ||
          (r.property?.title?.en || r.property?.title || "").toLowerCase().includes(term) ||
          (
            r.client?.fullName ||
            [r.client?.firstName, r.client?.lastName].filter(Boolean).join(" ") ||
            ""
          )
            .toLowerCase()
            .includes(term);
        if (!match) return false;
      }
      if (processFilterType === "missing" && (r.unreplacedMissingCount || 0) === 0) return false;
      if (
        processFilterType === "damaged" &&
        (r.unsentDamagedCount || 0) === 0 &&
        (r.unmaintainedDamagedCount || 0) === 0
      )
        return false;
      if (processFilterStatus === "replace" && (r.unreplacedMissingCount || 0) === 0) return false;
      if (processFilterStatus === "cost" && (r.unsentDamagedCount || 0) === 0) return false;
      if (processFilterStatus === "maintain" && (r.unmaintainedDamagedCount || 0) === 0)
        return false;
      return true;
    });
  }, [reservations, processSearch, processFilterType, processFilterStatus]);

  const filteredReport = useMemo(() => {
    return report.filter((row) => {
      if (filterType !== "all" && row.type !== filterType) return false;
      if (filterStatus !== "all" && row.status !== filterStatus) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const match =
          (row.reservationNumber || "").toLowerCase().includes(term) ||
          (row.propertyTitle || "").toLowerCase().includes(term) ||
          (row.clientName || "").toLowerCase().includes(term) ||
          (row.itemName || "").toLowerCase().includes(term);
        if (!match) return false;
      }
      return true;
    });
  }, [report, filterType, filterStatus, searchTerm]);

  const reportStats = useMemo(() => {
    const replaced = report.filter((r) => r.type === "Replaced");
    const damaged = report.filter((r) => r.type === "Damaged");
    const resolved = report.filter(
      (r) => r.status === "Replaced" || r.status === "Cost sent" || r.status === "Maintained"
    ).length;
    const uniqueReservations = new Set(report.map((r) => r.reservationId)).size;
    const missingAmount = replaced.reduce((s, r) => s + (r.amount || 0), 0);
    const damagedAmount = damaged.reduce((s, r) => s + (r.amount || 0), 0);
    const completionRate = report.length > 0 ? Math.round((resolved / report.length) * 100) : 0;
    return {
      total: report.length,
      replaced: replaced.length,
      damaged: damaged.length,
      pendingReplaced: replaced.filter((r) => r.status === "Pending").length,
      pendingDamaged: damaged.filter((r) => r.status === "Pending" || r.status === "Cost sent")
        .length,
      totalAmount: report.reduce((sum, r) => sum + (r.amount || 0), 0),
      missingAmount,
      damagedAmount,
      uniqueReservations,
      completionRate,
    };
  }, [report]);

  const filteredStats = useMemo(() => {
    const amount = filteredReport.reduce((s, r) => s + (r.amount || 0), 0);
    return { count: filteredReport.length, amount };
  }, [filteredReport]);

  const hasPending = useMemo(() => {
    const p = reservations.reduce(
      (acc, r) =>
        acc +
        (r.unreplacedMissingCount || 0) +
        (r.unsentDamagedCount || 0) +
        (r.unmaintainedDamagedCount || 0),
      0
    );
    return p > 0;
  }, [reservations]);

  if (loading && tabValue === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" gutterBottom>
              Items to Replace
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Replace missing items, process damaged (cost & resolved), view full report
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate(backRoute)}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back
          </MDButton>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {hasPending && (
          <Alert
            severity="warning"
            icon={<Icon>warning_amber</Icon>}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <MDTypography variant="subtitle2" fontWeight="bold">
              Items need your attention
            </MDTypography>
            <MDTypography variant="body2">
              Switch to <strong>To Process</strong> tab to mark items as replaced, send damaged
              costs, or mark as maintained.
            </MDTypography>
          </Alert>
        )}

        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="To Process" icon={<Icon fontSize="small">build</Icon>} iconPosition="start" />
          <Tab
            label="Full Report"
            icon={<Icon fontSize="small">assignment</Icon>}
            iconPosition="start"
          />
        </Tabs>

        {/* Tab 0: To Process */}
        {tabValue === 0 && (
          <>
            <Card sx={{ mb: 3, p: 2.5, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <MDTypography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing={1}
                display="block"
                mb={2}
              >
                Filter Reservations
              </MDTypography>
              <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search reservation, property, client..."
                  value={processSearch}
                  onChange={(e) => setProcessSearch(e.target.value)}
                  sx={{ minWidth: 260, ...filterInputHeight }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: "text.secondary", fontSize: 20 }}>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 160, ...filterInputHeight }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={processFilterType}
                    label="Type"
                    onChange={(e) => setProcessFilterType(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="missing">Missing</MenuItem>
                    <MenuItem value="damaged">Damaged</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, ...filterInputHeight }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={processFilterStatus}
                    label="Status"
                    onChange={(e) => setProcessFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="replace">To replace</MenuItem>
                    <MenuItem value="cost">Cost needed</MenuItem>
                    <MenuItem value="maintain">To maintain</MenuItem>
                  </Select>
                </FormControl>
                <MDBox ml="auto" display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption" color="text.secondary">
                    {filteredReservations.length} of {reservations.length} reservations
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
            <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
              <MDBox p={3}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="warning">build</Icon>
                  Reservations with items to process
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={2}>
                  Missing: mark as replaced. Damaged: enter cost and send email, then mark as
                  maintained when handled.
                </MDTypography>
              </MDBox>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Reservation</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Property</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>To Replace</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Damaged</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReservations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <MDTypography variant="body2" color="text.secondary">
                            {reservations.length === 0
                              ? "No reservations with items to process."
                              : "No reservations match your filters."}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReservations.map((r) => (
                        <TableRow key={r._id} hover>
                          <TableCell>
                            <MDTypography variant="body2" fontWeight="medium">
                              {r.reservationNumber || r._id}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              {r.checkOut?.checkedOutAt
                                ? new Date(r.checkOut.checkedOutAt).toLocaleDateString()
                                : "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            {r.property?.title?.en || r.property?.title || "N/A"}
                          </TableCell>
                          <TableCell>
                            {r.client?.fullName ||
                              [r.client?.firstName, r.client?.lastName].filter(Boolean).join(" ") ||
                              "N/A"}
                          </TableCell>
                          <TableCell>
                            {r.unreplacedMissingCount > 0 ? (
                              <Chip
                                label={`${r.unreplacedMissingCount} to replace`}
                                size="small"
                                color="error"
                              />
                            ) : (
                              <Chip
                                label="All replaced"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {r.unsentDamagedCount > 0 ? (
                              <Chip
                                label={`${r.unsentDamagedCount} cost(s) needed`}
                                size="small"
                                color="warning"
                              />
                            ) : r.unmaintainedDamagedCount > 0 ? (
                              <Chip
                                label={`${r.unmaintainedDamagedCount} to maintain`}
                                size="small"
                                color="info"
                              />
                            ) : (
                              <Chip label="Done" size="small" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => handleOpenDetails(r)}
                              startIcon={<Icon sx={{ fontSize: "1rem" }}>edit</Icon>}
                            >
                              Process
                            </MDButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Tab 1: Full Report */}
        {tabValue === 1 && (
          <>
            {reportLoading ? (
              <MDBox py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </MDBox>
            ) : (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <ComplexStatisticsCard
                      color="info"
                      icon="inventory_2"
                      title="Total Items"
                      count={reportStats.total}
                      percentage={{
                        color: "info",
                        amount: reportStats.uniqueReservations,
                        label: "reservations affected",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <ComplexStatisticsCard
                      color="error"
                      icon="swap_horiz"
                      title="Missing (Replaced)"
                      count={reportStats.replaced}
                      percentage={{
                        color: reportStats.pendingReplaced > 0 ? "warning" : "success",
                        amount:
                          reportStats.pendingReplaced > 0
                            ? `${reportStats.pendingReplaced} pending`
                            : "All resolved",
                        label: "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <ComplexStatisticsCard
                      color="warning"
                      icon="build"
                      title="Damaged"
                      count={reportStats.damaged}
                      percentage={{
                        color: reportStats.pendingDamaged > 0 ? "warning" : "success",
                        amount:
                          reportStats.pendingDamaged > 0
                            ? `${reportStats.pendingDamaged} pending`
                            : "All resolved",
                        label: "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <ComplexStatisticsCard
                      color="success"
                      icon="payments"
                      title="Total Deducted"
                      count={formatCurrency(reportStats.totalAmount)}
                      percentage={{
                        color: "success",
                        amount: `${reportStats.completionRate}%`,
                        label: "completion rate",
                      }}
                    />
                  </Grid>
                </Grid>

                {(reportStats.missingAmount > 0 || reportStats.damagedAmount > 0) && (
                  <Card
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing={1}
                    >
                      Deduction Breakdown
                    </MDTypography>
                    <MDBox display="flex" gap={3} mt={1} flexWrap="wrap">
                      {reportStats.missingAmount > 0 && (
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: "error.main" }}
                          />
                          <MDTypography variant="body2">
                            Missing: {formatCurrency(reportStats.missingAmount)}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reportStats.damagedAmount > 0 && (
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: "warning.main" }}
                          />
                          <MDTypography variant="body2">
                            Damaged: {formatCurrency(reportStats.damagedAmount)}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                )}

                <Card
                  sx={{ mb: 3, p: 2.5, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
                >
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing={1}
                    display="block"
                    mb={2}
                  >
                    Filter Report
                  </MDTypography>
                  <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Search reservation, property, client, item..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ minWidth: 260, ...filterInputHeight }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon sx={{ color: "text.secondary", fontSize: 20 }}>search</Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160, ...filterInputHeight }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={filterType}
                        label="Type"
                        onChange={(e) => setFilterType(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="Replaced">Replaced</MenuItem>
                        <MenuItem value="Damaged">Damaged</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160, ...filterInputHeight }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        label="Status"
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Replaced">Replaced</MenuItem>
                        <MenuItem value="Cost sent">Cost sent</MenuItem>
                        <MenuItem value="Maintained">Maintained</MenuItem>
                      </Select>
                    </FormControl>
                    <MDBox ml="auto" display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="caption" color="text.secondary">
                        {filteredStats.count} of {report.length} items
                      </MDTypography>
                      {filteredStats.count > 0 && (
                        <Chip
                          label={formatCurrency(filteredStats.amount)}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </MDBox>
                  </MDBox>
                </Card>

                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <MDBox
                    px={3}
                    py={2.5}
                    sx={{ bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200" }}
                  >
                    <MDTypography
                      variant="h6"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon color="info" sx={{ fontSize: 24 }}>
                        table_chart
                      </Icon>
                      Item Details
                    </MDTypography>
                  </MDBox>
                  <TableContainer>
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.100" }}>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Reservation
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Property
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Item
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                            align="right"
                          >
                            Amount
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                          >
                            Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              py: 2,
                            }}
                            align="right"
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredReport.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                              <Icon sx={{ fontSize: 48, color: "grey.400", mb: 1 }}>inbox</Icon>
                              <MDTypography variant="h6" color="text.secondary" gutterBottom>
                                No items match your filters
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredReport.map((row, idx) => (
                            <TableRow
                              key={`${row.reservationId}-${row.type}-${idx}`}
                              hover
                              sx={{ "&:nth-of-type(even)": { bgcolor: "grey.50" } }}
                            >
                              <TableCell sx={{ py: 2 }}>
                                <MDTypography variant="body2" fontWeight="600" color="info.main">
                                  {row.reservationNumber || String(row.reservationId).slice(-6)}
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary">
                                  {formatDate(row.checkedOutAt)}
                                </MDTypography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>{row.propertyTitle}</TableCell>
                              <TableCell sx={{ py: 2 }}>{row.clientName}</TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip
                                  label={row.type}
                                  size="small"
                                  color={row.type === "Replaced" ? "error" : "warning"}
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2, maxWidth: 180 }}>
                                <MDTypography variant="body2" fontWeight="500">
                                  {row.itemName}
                                  {row.quantity > 1 && (
                                    <MDTypography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {" "}
                                      ×{row.quantity}
                                    </MDTypography>
                                  )}
                                </MDTypography>
                                {row.description && (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    noWrap
                                    sx={{ maxWidth: 180 }}
                                    title={row.description}
                                  >
                                    {row.description}
                                  </MDTypography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {row.amount > 0 ? (
                                  <MDTypography variant="body2" fontWeight="600" color="info.main">
                                    {formatCurrency(row.amount)}
                                  </MDTypography>
                                ) : (
                                  <MDTypography variant="caption" color="text.secondary">
                                    —
                                  </MDTypography>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip
                                  label={row.status}
                                  size="small"
                                  color={
                                    ["Pending", "Cost sent"].includes(row.status)
                                      ? "warning"
                                      : "success"
                                  }
                                  variant={
                                    ["Pending", "Cost sent"].includes(row.status)
                                      ? "filled"
                                      : "outlined"
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <MDTypography variant="caption">
                                  {row.status === "Replaced"
                                    ? formatDate(row.replacedAt)
                                    : row.status === "Maintained"
                                    ? formatDate(row.resolvedAt)
                                    : row.status === "Cost sent"
                                    ? formatDate(row.costSentAt)
                                    : "—"}
                                </MDTypography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <MDButton
                                  variant="text"
                                  color="info"
                                  size="small"
                                  onClick={() =>
                                    navigate(
                                      isSuperAdmin
                                        ? `/super-admin/reservations/${row.reservationId}`
                                        : `/admin/reservations/${row.reservationId}`
                                    )
                                  }
                                  endIcon={<Icon sx={{ fontSize: 16 }}>arrow_forward</Icon>}
                                  sx={{ fontWeight: 600 }}
                                >
                                  View
                                </MDButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </>
            )}
          </>
        )}

        {/* Process Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <span>
                {selectedReservation?.reservationNumber} –{" "}
                {selectedReservation?.property?.title?.en ||
                  selectedReservation?.property?.title ||
                  "N/A"}
              </span>
              <MDButton
                variant="text"
                color="inherit"
                onClick={handleCloseDialog}
                sx={{ minWidth: 0 }}
              >
                <Icon>close</Icon>
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            {selectedReservation && (
              <MDBox>
                {/* Check-in/Check-out Agent */}
                {(() => {
                  const agent =
                    selectedReservation.assignedCheckInOutAgent ||
                    selectedReservation.property?.assignedCheckInOutAgent;
                  return agent ? (
                    <MDBox
                      sx={{
                        bgcolor: "info.lighter",
                        p: 2,
                        borderRadius: 2,
                        mb: 3,
                        border: "1px solid",
                        borderColor: "info.light",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="info.dark"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Icon sx={{ fontSize: 18 }}>person</Icon>
                        Check-in/Check-out Agent (this property)
                      </MDTypography>
                      <MDBox display="flex" flexDirection="column" gap={0.5}>
                        <MDTypography variant="body2">
                          <strong>Name:</strong> {agent.fullName || agent.firstName || "—"}
                        </MDTypography>
                        <MDTypography variant="body2">
                          <strong>Email:</strong> {agent.email || "—"}
                        </MDTypography>
                        <MDTypography variant="body2">
                          <strong>Phone:</strong> {agent.phone || agent.companyNumber || "—"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  ) : null;
                })()}

                <MDBox sx={{ bgcolor: "grey.100", p: 2, borderRadius: 2, mb: 3 }}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                    Insurance Summary
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={0.5}>
                    <MDTypography variant="body2">
                      Total:{" "}
                      {formatCurrency(
                        selectedReservation.pricing?.insurancePrice ||
                          selectedReservation.property?.insurancePrice
                      )}
                    </MDTypography>
                    <MDTypography variant="body2" color="error.main">
                      Deducted (missing):{" "}
                      {formatCurrency(selectedReservation.checkOut?.insuranceDeduction)}
                    </MDTypography>
                    <MDTypography variant="body2" color="warning.main">
                      Deducted (damaged):{" "}
                      {formatCurrency(selectedReservation.checkOut?.damagedItemsDeduction)}
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="bold">
                      Remaining:{" "}
                      {formatCurrency(
                        Math.max(
                          0,
                          (selectedReservation.pricing?.insurancePrice ||
                            selectedReservation.property?.insurancePrice ||
                            0) -
                            (selectedReservation.checkOut?.insuranceDeduction || 0) -
                            (selectedReservation.checkOut?.damagedItemsDeduction || 0)
                        )
                      )}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                {(selectedReservation.checkOut?.missingItems?.length || 0) > 0 && (
                  <MDBox mb={3}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1} color="error.main">
                      Missing Items (to replace)
                    </MDTypography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {(selectedReservation.checkOut.missingItems || []).map((m, i) => (
                        <li key={i} style={{ marginBottom: 8 }}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={1}
                          >
                            <MDTypography variant="body2">
                              {m.name}
                              {m.quantity > 1 && ` (${m.quantity} pcs)`} –{" "}
                              {formatCurrency(m.clientPrice)}
                              {m.replaced && (
                                <Chip
                                  label="Replaced"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </MDTypography>
                            {!m.replaced && (
                              <MDButton
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => handleMarkReplaced(selectedReservation._id, i)}
                                disabled={submitting}
                              >
                                Mark as Replaced
                              </MDButton>
                            )}
                          </MDBox>
                        </li>
                      ))}
                    </Box>
                  </MDBox>
                )}

                <Divider sx={{ my: 2 }} />

                {(selectedReservation.checkOut?.damagedItems?.length || 0) > 0 && (
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1} color="warning.main">
                      Damaged Items
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" display="block" mb={2}>
                      Enter cost and send, then mark as maintained when the item is handled.
                    </MDTypography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: "none" }}>
                      {(selectedReservation.checkOut.damagedItems || []).map((d, i) => (
                        <li key={i} style={{ marginBottom: 12 }}>
                          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <MDTypography variant="body2" sx={{ minWidth: 140 }}>
                              {(typeof d.name === "string" ? d.name : d.name?.en) || "Item"}
                              {d.description && ` – ${d.description}`}
                            </MDTypography>
                            {d.resolved ? (
                              <Chip label="Maintained" size="small" color="success" />
                            ) : d.costSentAt ? (
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Chip
                                  label={`Sent ${formatCurrency(d.cost)}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                                <MDButton
                                  variant="outlined"
                                  color="success"
                                  size="small"
                                  onClick={() =>
                                    handleMarkDamagedMaintained(selectedReservation._id, i)
                                  }
                                  disabled={submitting}
                                >
                                  Mark as Maintained
                                </MDButton>
                              </MDBox>
                            ) : (
                              <TextField
                                type="number"
                                size="small"
                                label="Cost"
                                value={damagedCosts[i] ?? ""}
                                onChange={(e) => handleDamagedCostChange(i, e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 120 }}
                                disabled={submitting}
                              />
                            )}
                          </MDBox>
                        </li>
                      ))}
                    </Box>
                    <MDButton
                      variant="contained"
                      color="warning"
                      onClick={handleSendDamagedCosts}
                      disabled={
                        submitting ||
                        (selectedReservation.checkOut.damagedItems || []).every(
                          (d) => d.costSentAt
                        ) ||
                        !Object.values(damagedCosts).some((v) => v != null && v > 0)
                      }
                      startIcon={submitting ? <CircularProgress size={16} /> : <Icon>send</Icon>}
                      sx={{ mt: 1 }}
                    >
                      {(selectedReservation.checkOut.damagedItems || []).every((d) => d.costSentAt)
                        ? "Costs already sent"
                        : "Send Costs to Customer"}
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton onClick={handleCloseDialog}>Close</MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ItemsToProcess;
